// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Grid, IconButton, Paper, Snackbar, Switch,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
  TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/Key';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SmsIcon from '@mui/icons-material/Sms';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import UsbIcon from '@mui/icons-material/Usb';
import api from '../utils/api';
import ChampRecherche from '../components/ChampRecherche';
import { filterItems, locationText } from '../utils/recherche';
import ContractBlock from '../components/etablissement/BlocContrat';
import CommitmentsBlock from '../components/etablissement/BlocEngagements';
import ServersBlock from '../components/etablissement/BlocServeurs';
import RoomImportDialog from '../components/etablissement/DialogueImportChambres';
import SmsFallbackDialog from '../components/etablissement/DialogueRepliSms';
import DialogueSecret from '../components/etablissement/DialogueSecret';
import DialogueSite from '../components/etablissement/DialogueSite';
import useVigeosAccounts from '../components/etablissement/useComptesVigeos';
import { shortDate, dateForField } from '../utils/dates';
import { errorMessage } from '../utils/erreurs';
import {
  creationBody, updateBody, EMPTY_DIRECTOR, PLAUSIBLE_EMAIL, groupState,
  generatePassword, EMPTY_SITE,
} from '../components/etablissement/commun';
import { ROOM_TYPE_LABEL } from '../components/foyer/vocabulaire';

/**
 * Fiche d'un client établissement : tout ce qui le concerne, au même endroit.
 *
 * Résidents, consentements et comptes du personnel se gèrent depuis la console
 * du client (gestion.vigeos.fr), avec un jeton `pro`. L'API admin des logements
 * refuse en 409 toute écriture sur une chambre d'établissement
 * (`properties._refuse_if_b2b`) : elle n'a ni cloisonnement par site ni journal
 * d'audit. Seul l'import de chambres est offert ici, pour le jour de la pose où
 * la direction n'a pas encore de compte (`POST /pro/admin/sites/{id}/rooms/import`,
 * transactionnel et journalisé).
 */

export default function FacilityClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [org, setOrg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [active, setActive] = useState(true);
  const [initialActive, setInitialActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [tab, setTab] = useState(0);

  const [rooms, setRooms] = useState([]);
  const [servers, setServers] = useState({});
  const [vigeosAccounts, loadAccounts] = useVigeosAccounts();

  const [dialogueSite, setDialogueSite] = useState(null);
  const [dialogueDirection, setDialogueDirection] = useState(null);
  const [dialogueImport, setDialogueImport] = useState(false);
  const [siteSms, setSiteSms] = useState(null);
  const [roomsVersion, setRoomsVersion] = useState(0);
  // Le mot de passe en clair ne vit que là, le temps du dialogue.
  const [secret, setSecret] = useState(null);

  const [siteSearch, setSiteSearch] = useState('');
  const [roomSearch, setRoomSearch] = useState('');
  const [serverSearch, setServerSearch] = useState('');

  const sites = useMemo(() => org?.sites || [], [org]);

  /** Ouvre la préparation d'une clé de pose pour cet établissement. Le site
   *  passe par l'état du routeur, pas par l'URL : l'écran d'arrivée manipule
   *  un ordre de fabrication, dont aucune donnée ne doit finir dans
   *  l'historique, le journal nginx ou chez Cloudflare. */
  const prepareKey = (siteId) => navigate('/cles-de-pose', { state: { siteId } });

  const loadServers = useCallback(async (clientSites) => {
    const targets = (clientSites || []).filter((s) => s && s.id);
    if (!targets.length) return;
    setServers((state) => {
      const suite = { ...state };
      targets.forEach((s) => { suite[s.id] = { chargement: true, liste: [], erreur: '', routeAbsente: false }; });
      return suite;
    });

    await Promise.all(targets.map(async (s) => {
      let routeMissing = true;
      let items = [];
      let serverError = '';
      try {
        // `validateStatus` écarte l'intercepteur 401 d'axios : un refus sur
        // cette lecture secondaire ne doit pas effacer le jeton et renvoyer à
        // /login.
        const r = await api.get(`/pro/admin/sites/${s.id}/servers`,
          { validateStatus: (code) => code < 500 });
        if (r.status === 200) {
          // La route rend une liste nue ; une enveloppe `{servers: []}` est
          // tolérée, la charge est celle de `pro_maintenance.site_servers`.
          items = Array.isArray(r.data) ? r.data : (r.data?.servers || []);
          routeMissing = false;
        }
        // 401/403/404 : refusée à ce jeton, ou site hors périmètre. Affiché
        // comme un manque de droit, pas comme une panne.
      } catch (e) {
        // Seuls les 5xx et les coupures réseau arrivent ici.
        serverError = errorMessage(e, 'Serveurs illisibles');
        routeMissing = false;
      }
      setServers((state) => ({
        ...state,
        [s.id]: { chargement: false, liste: items, erreur: serverError, routeAbsente: routeMissing },
      }));
    }));
  }, []);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/pro/admin/organisations/${id}`);
      const o = res.data;
      const fields = {
        name: o.name || '', contact_name: o.contact_name || '',
        contact_email: o.contact_email || '', contact_phone: o.contact_phone || '',
        contract_start: dateForField(o.contract_start), notes: o.notes || '',
      };
      setOrg(o);
      setInitial(fields);
      setFormValues({ ...fields });
      setActive(o.is_active !== false);
      setInitialActive(o.is_active !== false);
      setError('');
      loadServers(o.sites || []);
    } catch (e) {
      setError(errorMessage(e, 'Impossible de charger la fiche du client'));
    } finally {
      setLoading(false);
    }
  }, [id, loadServers]);

  useEffect(() => { charger(); }, [charger]);

  // Chambres : l'API d'administration ne liste les logements que globalement
  // (`GET /properties/?scope=facility`), sans filtre par site ; le tri par
  // établissement du client se fait donc ici. Un parc B2B compte quelques
  // centaines de chambres. S'il grossit, c'est un paramètre `site_id` qu'il
  // faudra à la route, pas une pagination ici.
  useEffect(() => {
    if (!sites.length) { setRooms([]); return; }
    const ids = new Set(sites.map((s) => s.id));
    let alive = true;
    api.get('/properties/', { params: { scope: 'facility' } })
      .then((res) => {
        if (!alive) return;
        const allRooms = Array.isArray(res.data) ? res.data : [];
        setRooms(allRooms.filter((p) => ids.has(p.site_id)));
      })
      .catch(() => { if (alive) setRooms([]); });
    return () => { alive = false; };
  }, [sites, roomsVersion]);

  const siteName = useCallback(
    (siteId) => sites.find((s) => s.id === siteId)?.name || '—',
    [sites],
  );

  const filteredSites = useMemo(() => filterItems(sites, siteSearch, (s) => [
    s.name, s.address, s.city, s.postal_code, s.timezone, s.server_id,
  ]), [sites, siteSearch]);

  const filteredRooms = useMemo(() => filterItems(rooms, roomSearch, (p) => [
    p.name, p.room_number, p.resident_name, p.floor, ROOM_TYPE_LABEL[p.room_type] || p.room_type,
    siteName(p.site_id),
  ]), [rooms, roomSearch, siteName]);

  const filteredServers = useMemo(() => filterItems(sites, serverSearch, (s) => [
    s.name, s.city, s.postal_code, s.server_id,
    ...((servers[s.id]?.liste || []).map((k) => k.code)),
  ]), [sites, serverSearch, servers]);

  const save = async () => {
    if (!org || !formValues) return;
    setEnCours(true);
    setError('');
    try {
      const body = updateBody(initial, formValues, ['contract_start']);
      if (body.contract_start) body.contract_start = `${body.contract_start}T00:00:00`;
      if (active !== initialActive) body.is_active = active;
      if (!Object.keys(body).length) {
        setError('Aucune modification à enregistrer');
        return;
      }
      await api.patch(`/pro/admin/organisations/${org.id}`, body);
      setToast({ texte: 'Client mis à jour', gravite: 'success' });
      await charger();
    } catch (e) {
      setError(errorMessage(e, "Échec de l'enregistrement"));
    } finally {
      setEnCours(false);
    }
  };

  const openSiteCreation = () => {
    loadAccounts();
    setDialogueSite({
      mode: 'creation', clientNom: org.name, siteId: null,
      initial: { ...EMPTY_SITE }, valeurs: { ...EMPTY_SITE },
      actif: true, erreur: '', enCours: false,
    });
  };

  const openSiteEditor = (site) => {
    loadAccounts();
    const fields = {
      name: site.name || '', address: site.address || '', city: site.city || '',
      postal_code: site.postal_code || '', country: site.country || 'France',
      timezone: site.timezone || 'Europe/Paris', server_id: site.server_id || '',
      owner_user_id: site.owner_user_id || '',
    };
    setDialogueSite({
      mode: 'edition', clientNom: org.name, siteId: site.id,
      initial: fields, valeurs: { ...fields },
      actif: site.is_active !== false, actifInitial: site.is_active !== false,
      erreur: '', enCours: false,
    });
  };

  const saveSite = async () => {
    const d = dialogueSite;
    if (!d) return;
    setDialogueSite({ ...d, enCours: true, erreur: '' });
    try {
      if (d.mode === 'creation') {
        await api.post(`/pro/admin/organisations/${org.id}/sites`, creationBody(d.valeurs));
        setToast({ texte: `Établissement rattaché à ${org.name}`, gravite: 'success' });
      } else {
        const body = updateBody(d.initial, d.valeurs);
        if (d.actif !== d.actifInitial) body.is_active = d.actif;
        if (!Object.keys(body).length) {
          setDialogueSite({ ...d, enCours: false, erreur: 'Aucune modification à enregistrer' });
          return;
        }
        await api.patch(`/pro/admin/sites/${d.siteId}`, body);
        setToast({ texte: 'Établissement mis à jour', gravite: 'success' });
      }
      setDialogueSite(null);
      charger();
    } catch (e) {
      setDialogueSite({ ...d, enCours: false, erreur: errorMessage(e, "Échec de l'enregistrement") });
    }
  };

  const createDirector = async () => {
    const d = dialogueDirection;
    if (!d) return;
    setDialogueDirection({ ...d, enCours: true, erreur: '' });
    const email = d.valeurs.email.trim().toLowerCase();
    const password = generatePassword(email);
    try {
      await api.post(`/pro/admin/organisations/${org.id}/staff`, {
        email,
        full_name: d.valeurs.full_name.trim() || null,
        password: password,
        site_id: null,
      });
      setDialogueDirection(null);
      setSecret({ email, motDePasse: password, contexte: org.name });
    } catch (e) {
      setDialogueDirection({
        ...d, enCours: false,
        erreur: errorMessage(e, 'Échec de la création du compte de direction'),
      });
    }
  };

  const copier = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ texte: `${label} copié dans le presse-papiers`, gravite: 'success' });
    } catch {
      setToast({ texte: `Copie impossible, sélectionnez ${label.toLowerCase()} à la main`, gravite: 'warning' });
    }
  };

  if (loading && !org) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }
  if (!org) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clients')} sx={{ mb: 2 }}>
          Retour aux clients
        </Button>
        <Alert severity="error">{error || 'Client introuvable'}</Alert>
      </Box>
    );
  }

  const state = groupState(org);
  const totalRooms = sites.reduce((n, s) => n + (s.rooms || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/clients')}><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" fontWeight="bold">{org.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            Client établissement · {sites.length} établissement{sites.length > 1 ? 's' : ''}
            {' · '}{totalRooms} chambre{totalRooms > 1 ? 's' : ''}
            {org.contract_start ? ` · contrat depuis le ${shortDate(org.contract_start)}` : ''}
          </Typography>
        </Box>
        <Chip label={state.label} color={state.color} />
        <Tooltip title="Recharger">
          <span><IconButton onClick={charger} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
        <Button size="small" variant="outlined" startIcon={<ReceiptLongIcon />}
          onClick={() => navigate('/facturation')}>
          Facturation
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}
        variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Informations" />
        <Tab label={`Établissements (${sites.length})`} />
        <Tab label={`Chambres (${rooms.length})`} />
        <Tab label="Serveurs" />
        <Tab label="Contrat" />
        <Tab label="Engagements" />
      </Tabs>

      {/* Onglet « Informations » */}
      {tab === 0 && formValues && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Identité et contact</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" required label="Nom du client"
                    value={formValues.name}
                    onChange={(e) => setFormValues((v) => ({ ...v, name: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Contact chez le client"
                    value={formValues.contact_name}
                    onChange={(e) => setFormValues((v) => ({ ...v, contact_name: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Courriel du contact"
                    value={formValues.contact_email}
                    onChange={(e) => setFormValues((v) => ({ ...v, contact_email: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Téléphone du contact"
                    value={formValues.contact_phone}
                    onChange={(e) => setFormValues((v) => ({ ...v, contact_phone: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="date" label="Début de contrat"
                    InputLabelProps={{ shrink: true }}
                    value={formValues.contract_start}
                    onChange={(e) => setFormValues((v) => ({ ...v, contract_start: e.target.value }))}
                    helperText="L'API ne sait pas l'effacer, seulement la changer." />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Notes" multiline rows={2}
                    value={formValues.notes}
                    onChange={(e) => setFormValues((v) => ({ ...v, notes: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label={active ? 'Client actif' : 'Client désactivé'}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Un client désactivé n&apos;accepte plus de nouvel établissement ni de
                    compte de direction. Cela ne déconnecte personne et ne coupe aucun
                    établissement déjà en service : pour fermer un site, désactivez-le
                    lui-même depuis l&apos;onglet Établissements.
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="contained" onClick={save}
                  disabled={enCours || formValues.name.trim().length < 2}>
                  {enCours ? <CircularProgress size={18} /> : 'Enregistrer'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Accès du client</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                L&apos;API ne permet pas au back-office de lire les comptes d&apos;un client :
                cette fiche ne peut donc ni afficher la direction en place, ni dire si
                elle s&apos;est déjà connectée, ni lui reposer un mot de passe. Le bouton
                ci-dessous n&apos;amorce que le PREMIER compte, et l&apos;API le refuse si le
                client en a déjà un. En cas de mot de passe perdu, la direction passe
                par « mot de passe oublié » sur gestion.vigeos.fr.
              </Typography>
              <Button size="small" variant="outlined" startIcon={<KeyIcon />}
                disabled={org.is_active === false}
                onClick={() => setDialogueDirection({
                  valeurs: { ...EMPTY_DIRECTOR }, erreur: '', enCours: false,
                })}>
                Créer le compte de direction
              </Button>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Identité juridique et facturation</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="body2" color="text.secondary">
                SIRET, références Chorus, délais et mandats se saisissent au niveau de
                CHAQUE établissement, depuis l&apos;écran Facturation : un groupe
                gestionnaire pilote des établissements qui ont chacun leur SIRET et leur
                service liquidateur, et le SIRET du destinataire est la clé de routage
                du portail public.
              </Typography>
              {org.siret && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  SIRET du client : <strong>{org.siret}</strong>
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Onglet « Établissements » */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <ChampRecherche
              valeur={siteSearch} onChange={setSiteSearch}
              placeholder="Rechercher un établissement par nom, ville ou code postal"
              compte={filteredSites.length} total={sites.length} nomObjet="établissement"
            />
            <Button variant="contained" startIcon={<DomainAddIcon />}
              disabled={org.is_active === false} onClick={openSiteCreation}>
              Rattacher un établissement
            </Button>
          </Box>

          {sites.length === 0 ? (
            <Alert severity="warning">
              Ce client n&apos;a aucun établissement. Tant qu&apos;il n&apos;en a pas, sa direction
              ne voit rien en se connectant.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Établissement</TableCell>
                    <TableCell>Localisation</TableCell>
                    <TableCell>Fuseau</TableCell>
                    <TableCell>Serveur</TableCell>
                    <TableCell align="right">Chambres</TableCell>
                    <TableCell>État</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSites.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Aucun établissement ne correspond à cette recherche.
                      </TableCell>
                    </TableRow>
                  ) : filteredSites.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.name}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {[s.address, locationText(s.postal_code, s.city)].filter(Boolean).join(', ')
                          || 'Non renseignée'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{s.timezone}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{s.server_id || 'Non enrôlé'}</TableCell>
                      <TableCell align="right">{s.rooms ?? 0}</TableCell>
                      <TableCell>
                        <Chip size="small"
                          label={s.is_active === false ? 'Désactivé' : 'En service'}
                          color={s.is_active === false ? 'default' : 'success'} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Préparer une clé de pose pour cet établissement">
                          <span>
                            <IconButton size="small" onClick={() => prepareKey(s.id)}
                              disabled={s.is_active === false}>
                              <UsbIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Numéros de repli SMS de nuit">
                          <IconButton size="small" onClick={() => setSiteSms(s)}>
                            <SmsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier l'établissement">
                          <IconButton size="small" onClick={() => openSiteEditor(s)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Onglet « Chambres » */}
      {tab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <ChampRecherche
              valeur={roomSearch} onChange={setRoomSearch}
              placeholder="Rechercher une chambre par nom, numéro, résident ou établissement"
              compte={filteredRooms.length} total={rooms.length} nomObjet="chambre"
            />
            <Button variant="contained" startIcon={<UploadFileIcon />}
              disabled={!sites.some((s) => s.is_active !== false)}
              onClick={() => setDialogueImport(true)}>
              Importer des chambres
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Import seulement. Les chambres s&apos;importent ici le jour de la pose, ou
            depuis la console du client ; les résidents, leur consentement et toute
            correction de chambre se font depuis la console (gestion.vigeos.fr),
            cloisonnée par établissement et auditée ligne à ligne. Un résident importé
            naît sans consentement : sa chambre n&apos;est pas surveillée tant qu&apos;il
            n&apos;est pas recueilli.
          </Alert>

          {rooms.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              Aucune chambre déclarée sur les établissements de ce client. Importez-les
              ici ou depuis sa console.
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Chambre</TableCell>
                    <TableCell>N°</TableCell>
                    <TableCell>Résident</TableCell>
                    <TableCell>Étage</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Établissement</TableCell>
                    <TableCell>État</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRooms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Aucune chambre ne correspond à cette recherche.
                      </TableCell>
                    </TableRow>
                  ) : filteredRooms.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.room_number || '—'}</TableCell>
                      <TableCell>{p.resident_name || '—'}</TableCell>
                      <TableCell>{p.floor || '—'}</TableCell>
                      <TableCell>{ROOM_TYPE_LABEL[p.room_type] || p.room_type || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{siteName(p.site_id)}</TableCell>
                      <TableCell>
                        <Chip size="small"
                          label={p.is_active === false ? 'Désactivée' : 'En service'}
                          color={p.is_active === false ? 'default' : 'success'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Onglet « Serveurs » */}
      {tab === 3 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <ChampRecherche
              valeur={serverSearch} onChange={setServerSearch}
              placeholder="Rechercher par établissement, ville ou code du serveur"
              compte={filteredServers.length} total={sites.length} nomObjet="établissement"
            />
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => loadServers(sites)}>
              Rafraîchir
            </Button>
          </Box>

          {sites.length === 0 ? (
            <Alert severity="warning">
              Aucun établissement, donc aucun serveur. Rattachez d&apos;abord un
              établissement.
            </Alert>
          ) : filteredServers.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              Aucun établissement ne correspond à cette recherche.
            </Paper>
          ) : filteredServers.map((s) => (
            <ServersBlock key={s.id} site={s} etat={servers[s.id]} onPreparerCle={prepareKey} />
          ))}
        </Box>
      )}

      {/* Onglet « Contrat » */}
      {tab === 4 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Un contrat par établissement. La recette fait courir l&apos;engagement ; la fin
            de contrat suit l&apos;ordre de l&apos;article 8.7 : dénonciation, instruction écrite,
            clôture, restitution, effacement certifié. Réservé aux comptes qui gèrent
            l&apos;équipe.
          </Alert>
          {sites.length === 0 ? (
            <Alert severity="warning">Aucun établissement, donc aucun contrat.</Alert>
          ) : sites.map((s) => (
            <ContractBlock key={s.id} site={s}
              onToast={(text) => { setToast({ texte: text, gravite: 'success' }); charger(); }} />
          ))}
        </Box>
      )}

      {/* Onglet « Engagements » */}
      {tab === 5 && (
        <Box>
          {sites.length === 0 ? (
            <Alert severity="warning">Aucun établissement, donc aucun engagement.</Alert>
          ) : sites.map((s) => (
            <CommitmentsBlock key={s.id} site={s}
              chambres={rooms.filter((c) => c.site_id === s.id)}
              onToast={(text) => setToast({ texte: text, gravite: 'success' })} />
          ))}
        </Box>
      )}

      <RoomImportDialog
        ouvert={dialogueImport}
        sites={sites}
        onFermer={() => setDialogueImport(false)}
        onImporte={() => { setRoomsVersion((v) => v + 1); charger(); }}
      />

      <SmsFallbackDialog
        site={siteSms}
        onFermer={() => setSiteSms(null)}
        onToast={(text) => setToast({ texte: text, gravite: 'success' })}
      />

      <DialogueSite
        dialogue={dialogueSite}
        comptesVigeos={vigeosAccounts}
        onChange={(patch) => setDialogueSite((d) => (d ? { ...d, ...patch } : d))}
        onFermer={() => setDialogueSite(null)}
        onEnregistrer={saveSite}
      />

      {/* Amorçage du compte de direction */}
      <Dialog open={!!dialogueDirection}
        onClose={() => !dialogueDirection?.enCours && setDialogueDirection(null)}
        maxWidth="sm" fullWidth>
        <DialogTitle>Compte de direction de {org.name}</DialogTitle>
        <DialogContent dividers>
          {dialogueDirection && (
            <>
              {dialogueDirection.erreur && (
                <Alert severity="error" sx={{ mb: 2 }}>{dialogueDirection.erreur}</Alert>
              )}
              <Alert severity="info" sx={{ mb: 2 }}>
                Amorçage seulement. Si ce client a déjà une direction, l&apos;API refuse :
                les comptes suivants se créent depuis la console du client.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" required type="email"
                    label="Courriel de la direction"
                    value={dialogueDirection.valeurs.email}
                    onChange={(e) => setDialogueDirection((d) => ({
                      ...d, valeurs: { ...d.valeurs, email: e.target.value },
                    }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Nom et prénom"
                    value={dialogueDirection.valeurs.full_name}
                    onChange={(e) => setDialogueDirection((d) => ({
                      ...d, valeurs: { ...d.valeurs, full_name: e.target.value },
                    }))} />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogueDirection(null)} disabled={dialogueDirection?.enCours}>
            Annuler
          </Button>
          <Button variant="contained" onClick={createDirector}
            disabled={!dialogueDirection || dialogueDirection.enCours
              || !PLAUSIBLE_EMAIL.test(dialogueDirection.valeurs.email.trim())}>
            {dialogueDirection?.enCours ? <CircularProgress size={18} /> : 'Créer le compte'}
          </Button>
        </DialogActions>
      </Dialog>

      <DialogueSecret secret={secret} onFermer={() => setSecret(null)} onCopier={copier} />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.gravite || 'success'} onClose={() => setToast(null)}>
          {toast?.texte}
        </Alert>
      </Snackbar>
    </Box>
  );
}
